import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../../context/AuthContext';
import axios from 'axios';
import {
  Upload,
  Search,
  Clock,
  RefreshCw,
  Eye,
  CheckCircle,
  XCircle,
  FileText,
  Image,
} from 'lucide-react';
import { Input } from '../../../components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../../components/ui/select';
import { Button } from '../../../components/ui/button';

// Normalize backend URL to prevent double /api paths
const rawBackendUrl = (process.env.REACT_APP_BACKEND_URL || '').trim().replace(/\/$/, '');
const BACKEND_URL = rawBackendUrl.replace(/\/api$/, '');
const API = `${BACKEND_URL}/api`;

const statusConfig = {
  pending: { label: 'Pending Review', color: 'bg-amber-100 text-amber-700', icon: Clock },
  verified: { label: 'Verified', color: 'bg-emerald-100 text-emerald-700', icon: CheckCircle },
  rejected: { label: 'Rejected', color: 'bg-red-100 text-red-700', icon: XCircle },
};

const StaffUploadsQueuePage = () => {
  const { token } = useAuth();
  const [uploads, setUploads] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('pending');

  const fetchUploads = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (statusFilter !== 'all') params.append('verification_status', statusFilter);
      params.append('limit', '50');

      const response = await axios.get(`${API}/portal/staff/queue/uploads?${params}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setUploads(response.data.items || []);
      setTotal(response.data.total || 0);
    } catch (err) {
      console.error('Failed to fetch uploads:', err);
      setError(err.response?.data?.detail || 'Failed to load uploads');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (token) fetchUploads();
  }, [token, statusFilter]);

  const filteredUploads = uploads.filter(u => {
    if (!searchTerm) return true;
    const fileName = u.file_name?.toLowerCase() || '';
    const docType = u.document_type?.toLowerCase() || '';
    return fileName.includes(searchTerm.toLowerCase()) || 
           docType.includes(searchTerm.toLowerCase());
  });

  const getFileIcon = (mimeType) => {
    if (mimeType?.startsWith('image/')) return Image;
    return FileText;
  };

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700">
        {error}
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="staff-uploads-queue">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-stone-900">Upload Review</h1>
          <p className="text-slate-500 mt-1">Verify client-uploaded documents</p>
        </div>
        <Button onClick={fetchUploads} variant="outline" size="sm">
          <RefreshCw size={16} className="mr-2" />
          Refresh
        </Button>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-4">
        <div className="flex-1 min-w-[200px] relative">
          <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input
            placeholder="Search by file name or type..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
          />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-44">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Status</SelectItem>
            <SelectItem value="pending">Pending Review</SelectItem>
            <SelectItem value="verified">Verified</SelectItem>
            <SelectItem value="rejected">Rejected</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Stats */}
      <div className="flex items-center gap-4 text-sm text-slate-500">
        <span>Showing {filteredUploads.length} of {total} uploads</span>
      </div>

      {/* Uploads Grid */}
      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {[...Array(6)].map((_, i) => (
            <div key={i} className="bg-white rounded-xl border border-slate-200 p-5 animate-pulse">
              <div className="h-20 bg-slate-100 rounded mb-4" />
              <div className="h-4 bg-slate-100 rounded w-3/4 mb-2" />
              <div className="h-3 bg-slate-100 rounded w-1/2" />
            </div>
          ))}
        </div>
      ) : filteredUploads.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredUploads.map((upload) => {
            const status = statusConfig[upload.verification_status] || statusConfig.pending;
            const StatusIcon = status.icon;
            const FileIcon = getFileIcon(upload.mime_type);
            return (
              <div
                key={upload.upload_id}
                className="bg-white rounded-xl border border-slate-200 p-5 hover:shadow-md transition-shadow"
                data-testid={`upload-card-${upload.upload_id}`}
              >
                <div className="flex items-start justify-between mb-4">
                  <div className="w-12 h-12 rounded-lg bg-slate-100 flex items-center justify-center">
                    <FileIcon size={24} className="text-slate-600" />
                  </div>
                  <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${status.color}`}>
                    <StatusIcon size={12} />
                    {status.label}
                  </span>
                </div>

                <h3 className="font-medium text-stone-900 mb-1 truncate" title={upload.file_name}>
                  {upload.file_name || 'Untitled'}
                </h3>
                <p className="text-sm text-slate-500 mb-3">
                  {upload.document_type || 'Document'}
                </p>

                <div className="flex items-center justify-between text-xs text-slate-400 pt-3 border-t border-slate-100">
                  <span>
                    {upload.created_at ? new Date(upload.created_at).toLocaleDateString() : '—'}
                  </span>
                  <Link 
                    to={`/staff/cases/${upload.case_id}/uploads`}
                    className="text-emerald-600 hover:text-emerald-700 font-medium"
                  >
                    Review
                  </Link>
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-slate-200 p-12 text-center">
          <Upload size={48} className="mx-auto text-slate-300 mb-4" />
          <h3 className="text-lg font-semibold text-stone-900 mb-2">No uploads pending</h3>
          <p className="text-slate-500">
            {searchTerm || statusFilter !== 'pending'
              ? 'Try adjusting your filters'
              : 'All uploads have been reviewed'}
          </p>
        </div>
      )}
    </div>
  );
};

export default StaffUploadsQueuePage;
